import { useEffect, useState } from 'react';
import { ShieldCheck, Plus, Loader2 } from 'lucide-react';
import Card from '@/components/ui/Card';
import Button from '@/components/ui/Button';
import Input from '@/components/ui/Input';
import Modal from '@/components/ui/Modal';
import api from '@/lib/api';

interface ExclusionRow {
  id: string;
  user_id: string;
  starts_at: string;
  ends_at: string | null;
  reason: string | null;
  source: 'user' | 'operator';
  created_at: string;
  email: string;
  first_name: string | null;
  last_name: string | null;
}

interface ListResponse {
  exclusions: ExclusionRow[];
}

function formatRange(starts: string, ends: string | null): string {
  const s = new Date(starts).toLocaleDateString();
  if (!ends) return `${s} → permanent`;
  return `${s} → ${new Date(ends).toLocaleDateString()}`;
}

export default function SelfExclusions() {
  const [rows, setRows] = useState<ExclusionRow[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [showLog, setShowLog] = useState(false);
  const [submitting, setSubmitting] = useState(false);

  // Operator-log form state
  const [userId, setUserId] = useState('');
  const [days, setDays] = useState<string>(''); // empty string = permanent
  const [reason, setReason] = useState('');

  async function load() {
    setLoading(true);
    setError(null);
    try {
      const res = await api.get<ListResponse>('/admin/social-casino/self-exclusions');
      setRows(res.exclusions);
    } catch (e: any) {
      setError(e.message || 'Failed to load self-exclusions');
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => {
    load();
  }, []);

  async function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    setSubmitting(true);
    try {
      await api.post('/admin/social-casino/self-exclusions', {
        user_id: userId.trim(),
        duration_days: days === '' ? null : Number(days),
        reason: reason.trim(),
      });
      setShowLog(false);
      setUserId('');
      setDays('');
      setReason('');
      await load();
    } catch (e: any) {
      setError(e.message || 'Failed to log exclusion');
    } finally {
      setSubmitting(false);
    }
  }

  return (
    <div className="space-y-6">
      <div className="flex items-start justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold flex items-center gap-2">
            <ShieldCheck className="w-6 h-6 text-emerald-600" />
            Responsible-play self-exclusions
          </h1>
          <p className="text-sm text-text-muted mt-1 max-w-2xl">
            Active exclusions. Players who request a break — either self-serve
            via the account page or through support — appear here. The
            embedded game polls each user's status at session start.
          </p>
        </div>
        <Button onClick={() => setShowLog(true)} variant="primary" size="md">
          <Plus className="w-4 h-4" />
          Log exclusion
        </Button>
      </div>

      {error && (
        <div role="alert" className="bg-rose-50 border border-rose-200 text-rose-900 rounded-lg p-3 text-sm">
          {error}
        </div>
      )}

      <Card>
        {loading ? (
          <div className="flex items-center justify-center py-12 text-text-muted">
            <Loader2 className="w-5 h-5 animate-spin mr-2" />
            Loading…
          </div>
        ) : rows.length === 0 ? (
          <div className="text-center py-12 text-text-muted text-sm">
            No active self-exclusions.
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="text-left text-text-muted text-xs uppercase tracking-wider">
                <tr>
                  <th className="px-4 py-2">Player</th>
                  <th className="px-4 py-2">Window</th>
                  <th className="px-4 py-2">Source</th>
                  <th className="px-4 py-2">Reason</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-stone-100">
                {rows.map((r) => (
                  <tr key={r.id}>
                    <td className="px-4 py-3">
                      <div className="font-medium text-text-primary">
                        {r.first_name || r.last_name
                          ? `${r.first_name ?? ''} ${r.last_name ?? ''}`.trim()
                          : r.email}
                      </div>
                      <div className="text-xs text-text-muted">{r.email}</div>
                    </td>
                    <td className="px-4 py-3">{formatRange(r.starts_at, r.ends_at)}</td>
                    <td className="px-4 py-3">
                      <span
                        className={
                          r.source === 'operator'
                            ? 'inline-block px-2 py-0.5 rounded text-xs bg-amber-100 text-amber-800'
                            : 'inline-block px-2 py-0.5 rounded text-xs bg-stone-100 text-stone-700'
                        }
                      >
                        {r.source}
                      </span>
                    </td>
                    <td className="px-4 py-3 text-text-muted">{r.reason || '—'}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </Card>

      {showLog && (
        <Modal isOpen={showLog} onClose={() => setShowLog(false)} title="Log self-exclusion">
          <form onSubmit={handleSubmit} className="space-y-4">
            <div>
              <label className="block text-sm font-medium mb-1">User ID</label>
              <Input
                value={userId}
                onChange={(e) => setUserId(e.target.value)}
                placeholder="UUID of the user"
                required
              />
              <p className="text-xs text-text-muted mt-1">
                Look up the user in /admin/customers and paste their ID.
              </p>
            </div>
            <div>
              <label className="block text-sm font-medium mb-1">Duration (days)</label>
              <Input
                type="number"
                min={1}
                max={3650}
                value={days}
                onChange={(e) => setDays(e.target.value)}
                placeholder="Leave blank for permanent"
              />
            </div>
            <div>
              <label className="block text-sm font-medium mb-1">Reason</label>
              <Input
                value={reason}
                onChange={(e) => setReason(e.target.value)}
                placeholder="Support ticket #1234 — player asked for a break"
                required
              />
            </div>
            <div className="flex gap-2 justify-end pt-2">
              <Button type="button" variant="outline" onClick={() => setShowLog(false)}>
                Cancel
              </Button>
              <Button type="submit" variant="primary" disabled={submitting}>
                {submitting ? <Loader2 className="w-4 h-4 animate-spin" /> : 'Log exclusion'}
              </Button>
            </div>
          </form>
        </Modal>
      )}
    </div>
  );
}
